// 公共接口 API
import { http } from './request'
import type {
  CaptchaResult,
  LoginParams,
  LoginResult,
  UserInfo,
  ChangePasswordParams,
  SetPasswordParams,
  WxAuthUrlParams,
  WxAuthUrlResult,
  WxJsApiSignatureParams,
  WxJsApiSignatureResult,
  WorkWxConfigParams,
  WorkWxConfigResult,
  WorkWxAgentConfigParams,
  WorkWxAgentConfigResult,
  WorkLoginParams,
  WorkLoginResult,
  WorkWxAuthUrlParams,
  WorkWxAuthUrlResult,
  WorkCodeLoginParams,
  WorkCodeLoginResult,
  StoreListResult,
  MyStoreListResult,
  SubordinateStoreListResult,
  SetCurrentStoreParams,
  SetCurrentStoreResult,
} from './types/common'

/**
 * 生成验证码
 */
export const fetchCaptcha = (): Promise<CaptchaResult> => {
  return http.post('/app/common/captcha')
}

/**
 * 用户登录
 */
export const postLogin = (params: LoginParams): Promise<LoginResult> => {
  return http.post('/app/common/login', params)
}

/**
 * 用户登出
 */
export const postLogout = (): Promise<void> => {
  return http.post('/app/common/logout')
}

/**
 * 查询当前用户信息
 * 401时不显示Toast，不自动跳转登录页
 */
export const fetchUserInfo = (): Promise<UserInfo> => {
  return http.get('/app/common/userInfo', {
    skipAuthToast: true,      // 401时不显示"未授权"提示
    skipAuthRedirect: true,   // 401时不自动跳转登录页
  })
}

/**
 * 修改密码
 */
export const changePassword = (params: ChangePasswordParams): Promise<void> => {
  return http.post('/app/common/changePassword', params)
}

/**
 * 设置密码
 */
export const setPassword = (params: SetPasswordParams): Promise<void> => {
  return http.post('/app/common/setPassword', params)
}

/**
 * 获取微信授权URL
 * POST /wx/authUrl
 * @param params - 授权参数
 * @param params.scope - 授权作用域：snsapi_base 或 snsapi_userinfo
 * @param params.state - 可选，自定义状态参数。以 "develop" 开头使用开发环境客户端URL，否则使用生产环境客户端URL
 */
export const fetchWxAuthUrl = (params: WxAuthUrlParams): Promise<WxAuthUrlResult> => {
  return http.post('/wx/authUrl', params, {
    skipAuthRedirect: true,
    skipAuthToast: true,
  })
}

/**
 * 获取微信 JS-SDK 签名
 * POST /wx/jsSdkConfig
 * @param params - 签名参数
 * @param params.url - 当前页面 URL（不含 # 及其后面部分）
 */
export const fetchWxJsApiSignature = (params: WxJsApiSignatureParams): Promise<WxJsApiSignatureResult> => {
  return http.post('/wx/jsSdkConfig', params, {
    skipAuthRedirect: true,
    skipAuthToast: true,
  })
}

/**
 * 获取企业微信 config 签名（用于 wx.config，基于 jsapi_ticket）
 * POST /wx/workConfig
 * @param params - 签名参数
 * @param params.url - 当前页面 URL（不含 # 及其后面部分）
 */
export const fetchWorkWxConfig = (params: WorkWxConfigParams): Promise<WorkWxConfigResult> => {
  return http.post('/wx/workConfig', params, {
    skipAuthRedirect: true,
    skipAuthToast: true,
  })
}

/**
 * 获取企业微信 agentConfig 签名（用于 wx.agentConfig，基于 agent_ticket）
 * POST /wx/workAgentConfig
 * @param params - 签名参数
 * @param params.url - 当前页面 URL（不含 # 及其后面部分）
 */
export const fetchWorkWxAgentConfig = (params: WorkWxAgentConfigParams): Promise<WorkWxAgentConfigResult> => {
  return http.post('/wx/workAgentConfig', params, {
    skipAuthRedirect: true,
    skipAuthToast: true,
  })
}

/**
 * 企业微信静默登录
 * POST /wx/workLogin
 * @param params - 登录参数
 * @param params.userId - 企业微信用户 userId（从 wx.agentConfig 获取）
 */
export const fetchWorkLogin = (params: WorkLoginParams): Promise<WorkLoginResult> => {
  return http.post('/wx/workLogin', params, {
    skipAuthRedirect: true,
    skipAuthToast: true,
  })
}

/**
 * 获取企业微信网页授权链接
 * POST /wx/workAuthUrl
 * @param params - 授权参数
 * @param params.redirectUrl - 可选，授权后重定向的前端页面地址。建议传入当前页面地址
 * @param params.state - 可选，自定义状态参数，用于防止 CSRF 攻击
 */
export const fetchWorkWxAuthUrl = (params: WorkWxAuthUrlParams): Promise<WorkWxAuthUrlResult> => {
  return http.post('/wx/workAuthUrl', params, {
    skipAuthRedirect: true,
    skipAuthToast: true,
  })
}

/**
 * 通过企业微信网页授权码登录
 * POST /wx/workCodeLogin
 * @param params - 登录参数
 * @param params.code - 企业微信网页授权码（从重定向 URL 的 code 参数获取）
 */
export const fetchWorkCodeLogin = (params: WorkCodeLoginParams): Promise<WorkCodeLoginResult> => {
  return http.post('/wx/workCodeLogin', params, {
    skipAuthRedirect: true,
    skipAuthToast: true,
  })
}

/**
 * 查询门店列表
 * POST /api/app/common/storeList
 */
export const fetchStoreList = (): Promise<StoreListResult> => {
  return http.post('/app/common/storeList', {})
}

/**
 * 设置当前门店
 * POST /api/app/common/setCurrentStore
 */
export const setCurrentStore = (params: SetCurrentStoreParams): Promise<SetCurrentStoreResult> => {
  return http.post('/app/common/setCurrentStore', params)
}

/**
 * 查询当前员工有权限的门店列表
 * POST /api/app/common/myStoreList
 */
export const fetchMyStoreList = (): Promise<MyStoreListResult> => {
  return http.post('/app/common/myStoreList', {})
}

/**
 * 查询指定门店的下级门店列表（包含直接下级和下下级）
 * POST /api/app/common/subordinateStores
 * @param storeId 上级门店ID
 */
export const fetchSubordinateStores = (storeId: string): Promise<SubordinateStoreListResult> => {
  return http.post('/app/common/subordinateStores', { storeId })
}
